import classNames from 'classnames';
import { MouseEvent, PropsWithChildren, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { getAssetUrl } from '../../../utils/config';
import useIsMobileDevice from '../../../utils/useIsMobileDevice';
import './style.scss';

type Direction = 'top-left' | 'top' | 'top-right' | 'bottom-right' | 'bottom' | 'bottom-left';
export type ToolTipProps = {
  headline?: ReactNode;
  hideHeadlineIcon?: boolean;
  content?: ReactNode;
  centered?: boolean;
  dataTestId?: string;
};

const viewportHeight = window.innerHeight;
const viewportWidth = window.innerWidth;

const ToolTip = ({
  headline,
  hideHeadlineIcon,
  content,
  centered,
  dataTestId,
  children
}: PropsWithChildren<ToolTipProps>) => {
  const [isVisible, setIsVisible] = useState(false);
  const [direction, setDirection] = useState<Direction>('top-left');
  const isMobile = useIsMobileDevice();
  const toolTipRef = useRef<HTMLImageElement>(null);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (isMobile) {
      setIsVisible(!isVisible);
    }
  };

  let throttleTimeout = false;
  const getTooltipDirection = () => {
    const position = toolTipRef.current?.getBoundingClientRect();
    if (!position) {
      return;
    }

    const showBelow = position.top < viewportHeight / 2;
    const showAfter = position.left < viewportWidth / 2;
    const thirdOfViewportWidth = viewportWidth / 3;
    const showCentered =
      centered ||
      (isMobile && thirdOfViewportWidth < position.left && position.left < viewportWidth - thirdOfViewportWidth);

    if (showBelow) {
      setDirection(
        showCentered ? 'bottom'
        : showAfter ? 'bottom-right'
        : 'bottom-left'
      );
    } else {
      setDirection(
        showCentered ? 'top'
        : showAfter ? 'top-right'
        : 'top-left'
      );
    }

    throttleTimeout = false;
  };

  useLayoutEffect(() => {
    const handleScroll = () => {
      if (!throttleTimeout) {
        throttleTimeout = true;
        setTimeout(getTooltipDirection, 1);
      } else {
        getTooltipDirection();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useLayoutEffect(() => {
    getTooltipDirection();
  }, []);

  return (
    <div
      ref={toolTipRef}
      className="tool-tip"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onClick={e => handleClick(e)}
      data-test-id={`tool-tip-${dataTestId}`}
    >
      {children ? children : <img className="tool-tip__icon" src={getAssetUrl('common/infoFill.svg')} />}
      <div
        className={classNames('tool-tip__content', `tool-tip__content--${direction}`, {
          'tool-tip__content--visible': isVisible
        })}
      >
        {headline && (
          <div className="tool-tip__title">
            {!hideHeadlineIcon && <img className="tool-tip__icon title-icon" src={getAssetUrl('common/info.svg')} />}
            <div className="tool-tip__headline">{headline}</div>
          </div>
        )}
        {typeof content === 'string' ?
          <p className="tool-tip__text">{content}</p>
        : content}
      </div>
    </div>
  );
};
export default ToolTip;
